import { useEffect, useMemo, useState } from 'react';
import Modal from 'antd/lib/modal';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import CheckCircleFilled from '@ant-design/icons/lib/icons/CheckCircleFilled';
import ExclamationCircleOutlined from '@ant-design/icons/lib/icons/ExclamationCircleOutlined';
import ReloadOutlined from '@ant-design/icons/lib/icons/ReloadOutlined';
import SyncOutlined from '@ant-design/icons/lib/icons/SyncOutlined';
import Tooltip from 'antd/lib/tooltip';
import { PRIMARY_COLOR } from 'src/theme';
import { MINUTE_MS } from 'src/domain/commonConst';
import { useCustomerUtilityBillQuery } from 'src/domain/customer-utility-bill/queries';
import { useCustomerUtilityBillFileQuery } from 'src/domain/customer-utility-bill-file/queries';
import { BILL_BORDER, BILL_SURFACE_MUTED, EMPTY_VALUE } from '../billStyles';
import {
    ICustomerUtilityBill,
    UPDATABLE_BILL_FIELDS,
    billHasExtractedData,
    findBillMetaRow,
    getEditableBillFields,
    getMissingParsingSuccessFields,
    hasBillFieldErrors,
    isBillEditable,
    isBillReparsable,
    isReparseInProgress,
    validateBillFields,
} from 'src/domain/customer-utility-bill/interface';
import { getComputerFormattedTime } from 'src/domain/date/date';
import { defaultDateFormat } from '../../../domain/common/dateFormatters';
import { BillStatusTag } from '../BillStatusTag';
import { BillPdfPanel } from './BillPdfPanel';
import { BillSiteInfoSection } from './BillSiteInfoSection';
import { BillVendorSection } from './BillVendorSection';
import { BillAddressSection } from './BillAddressSection';
import { BillValuesSection } from './BillValuesSection';
import { IBillSectionProps } from './BillFormField';
import { BillReviewSection } from './BillReviewSection';

const { Text } = Typography;

const billPanelStyle = { borderTop: `1px solid ${BILL_BORDER}`, background: BILL_SURFACE_MUTED } as const;

interface Props {
    row: ICustomerUtilityBill;
    saving?: boolean;
    onClose: () => void;
    onSave: (updated: ICustomerUtilityBill, verified: boolean) => void;
    onReparse: (row: ICustomerUtilityBill) => Promise<boolean>;
}

export const EditBillModal = ({ row, saving, onClose, onSave, onReparse }: Props) => {
    const [form, setForm] = useState<ICustomerUtilityBill>(row);
    const [verified, setVerified] = useState(!!row.verifiedAt);

    // Once a re-parse is confirmed, poll the bill by id every minute until it reaches a terminal status
    // (parsed successfully, parsing failed, or needs review) so the modal reflects the outcome without the
    // operator having to close and reopen it.
    const [tracking, setTracking] = useState(false);
    // True only for the gap between confirming re-parse and the request settling (before `tracking` itself
    // reflects it) — closes the window where the re-parse/save buttons would otherwise stay clickable and let
    // the operator fire a second overlapping request for the same bill.
    const [requestingReparse, setRequestingReparse] = useState(false);
    const { data: liveBill } = useCustomerUtilityBillQuery(row.id, {
        enabled: tracking,
        refetchInterval: (bill?: ICustomerUtilityBill) => (bill && !isReparseInProgress(bill) ? false : MINUTE_MS),
    });
    // While tracking, the polled record supersedes the (now stale) prop for status/editability/timestamps.
    const effectiveRow = tracking && liveBill ? liveBill : row;
    const reparseDone = tracking && !!liveBill && !isReparseInProgress(liveBill);

    // The bill's source-file row (matched by meter + rate class) decides which fields are editable: values present in
    // the file are the source of truth (read-only); fields the file left empty — or every field when there's no
    // matching row — can be edited. While the file loads, nothing is editable; no file (or a failed load) = no match.
    const { data: sourceFile, isInitialLoading: isSourceFileLoading } = useCustomerUtilityBillFileQuery(row.fileId ?? '', {
        enabled: !!row.fileId,
    });
    const metaRow = useMemo(() => findBillMetaRow(row, sourceFile?.meta ?? []), [row, sourceFile]);
    const editableFields = useMemo(() => (isSourceFileLoading ? new Set<never>() : getEditableBillFields(metaRow)), [isSourceFileLoading, metaRow]);

    // The modal opens for any non-parsing record so the operator can view it, but only a parsed record can be saved.
    const editable = isBillEditable(effectiveRow);
    // Re-parse is only offered once the previous parsing flow has finished.
    const reparsable = isBillReparsable(effectiveRow);
    // Verification follows the *edited* data: with no extracted data the record would be FAILED and can't be verified.
    const canVerify = billHasExtractedData(form);
    // Mirror the backend field rules so an invalid edit is caught before the save round-trips.
    const fieldErrors = validateBillFields(form);
    const hasErrors = hasBillFieldErrors(fieldErrors);
    // Empty fields needed for Parsing Success keep a bill in Needs Review — warn, but don't block saving.
    const missingFields = useMemo(() => getMissingParsingSuccessFields(form), [form]);

    useEffect(() => {
        setForm(effectiveRow);
        setVerified(!!effectiveRow.verifiedAt);
    }, [effectiveRow]);

    // Clearing the data while "verified" is on would be rejected by the backend — keep the toggle honest.
    useEffect(() => {
        if (!canVerify) setVerified(false);
    }, [canVerify]);

    const handleChange = <K extends keyof ICustomerUtilityBill>(fieldName: K, value: ICustomerUtilityBill[K]) => {
        setForm(f => ({
            ...f,
            [fieldName]: value,
        }));
    };

    // Has the operator changed any editable field or the verify intent? Empty string and null are treated as
    // equal so focusing-then-clearing a blank field doesn't register as a change.
    const isDirty = useMemo(() => {
        const same = (a: string | number | null, b: string | number | null) => (a ?? '') === (b ?? '');
        return (
            UPDATABLE_BILL_FIELDS.some(field => !same(form[field], effectiveRow[field])) ||
            !same(form.notes, effectiveRow.notes) ||
            verified !== !!effectiveRow.verifiedAt
        );
    }, [form, verified, effectiveRow]);

    // Guard the close affordances (Cancel button, header X, mask click, Esc) so unsaved edits aren't lost silently.
    const handleClose = () => {
        if (!isDirty) {
            onClose();
            return;
        }
        Modal.confirm({
            title: 'Discard unsaved changes?',
            icon: <ExclamationCircleOutlined />,
            content: 'Your edits to this bill haven’t been saved. Closing will discard them.',
            okText: 'Discard',
            okButtonProps: { danger: true },
            onOk: onClose,
        });
    };

    const reparse = () => {
        Modal.confirm({
            title: 'Re-parse this bill?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <>
                    Re-parsing will overwrite manual edits to <b>zip code, kW, kWh, and billing period dates</b>.
                    {isDirty && (
                        <>
                            {' '}
                            <b>Your unsaved changes in this form will be discarded.</b>
                        </>
                    )}
                </>
            ),
            okText: 'Re-parse',
            okButtonProps: { danger: true },
            onOk: () => {
                // Reset first: if a prior reparse in this session already finished, `tracking` is still true,
                // and setting it to `true` again is a no-op for React — the query's `enabled` flag wouldn't
                // actually transition, so a second reparse would never resume polling. Dropping it to `false`
                // first guarantees a real false -> true flip once the request confirms the bill was queued.
                setTracking(false);
                // Lock the buttons for the gap between the request and `tracking` reflecting it — otherwise
                // they'd stay clickable and a second click could fire an overlapping reparse/save request.
                setRequestingReparse(true);
                // Wait for the backend to confirm the bill was actually queued before polling — starting the
                // poll in the same tick as the request risks the first fetch racing ahead of the write and
                // reading the bill's pre-reparse (already-terminal) status as if the reparse were already done.
                onReparse(row)
                    .then(queued => {
                        if (queued) setTracking(true);
                    })
                    .finally(() => setRequestingReparse(false));
            },
        });
    };

    const sectionProps: IBillSectionProps = { form, editableFields, errors: fieldErrors, missingFields, disabled: !editable, handleChange };

    // Build the header subtitle from whatever source fields are present (any can be null), so we never render
    // stray separators like "Store # · · Acct".
    const locationLine = [row.address, row.city, row.state].filter(Boolean).join(', ');
    const subtitle = [
        row.siteNumber ? `Store #${row.siteNumber}` : null,
        locationLine || null,
        row.accountNumber ? `Acct ${row.accountNumber}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <Modal
            open
            onCancel={handleClose}
            closable={!saving && !requestingReparse}
            maskClosable={!saving && !requestingReparse}
            keyboard={!saving && !requestingReparse}
            width="min(1400px, 95vw)"
            style={{ top: 20 }}
            styles={{ content: { overflow: 'hidden' }, body: { padding: 0, height: '80vh', display: 'flex' } }}
            footer={null}
            title={
                <Space size="middle" wrap>
                    <Text strong style={{ fontSize: 16 }}>
                        {row.siteName || 'Customer utility bill'}
                    </Text>
                    <BillStatusTag status={effectiveRow.processingStatus} />
                    {effectiveRow.verifiedAt && (
                        <Tooltip title={`Verified at ${defaultDateFormat(effectiveRow.verifiedAt)}`}>
                            <CheckCircleFilled style={{ color: PRIMARY_COLOR, fontSize: 16 }} />
                        </Tooltip>
                    )}
                    {subtitle && (
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                            {subtitle}
                        </Text>
                    )}
                </Space>
            }
        >
            <>
                <BillPdfPanel fileId={row.fileId} />

                <div style={{ width: '42%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                        {tracking && (
                            <Alert
                                style={{ marginBottom: 14 }}
                                type={reparseDone ? 'success' : 'info'}
                                showIcon
                                icon={reparseDone ? undefined : <SyncOutlined spin />}
                                message={reparseDone ? 'Re-parsing complete.' : 'Re-parsing this bill — refreshing every minute.'}
                            />
                        )}
                        {!isSourceFileLoading && (
                            <Alert
                                style={{ marginBottom: 14 }}
                                type="info"
                                showIcon
                                message={
                                    metaRow
                                        ? 'Matched to the source file row — fields filled from the file are read-only; AI-extracted fields are editable.'
                                        : 'No matching row in the source file (Meter # + Rate Class) — all fields were extracted by AI and are editable.'
                                }
                            />
                        )}
                        {missingFields.size > 0 && (
                            <Alert
                                style={{ marginBottom: 14 }}
                                type="warning"
                                showIcon
                                message="Some fields could not be extracted during parsing — the bill will remain in Needs Review until they are filled."
                            />
                        )}
                        <BillSiteInfoSection {...sectionProps} />
                        <BillVendorSection {...sectionProps} />
                        <BillAddressSection {...sectionProps} />
                        <BillValuesSection {...sectionProps} row={effectiveRow} />
                        <BillReviewSection
                            form={form}
                            handleChange={handleChange}
                            verified={verified}
                            onVerifiedChange={setVerified}
                            canVerify={canVerify}
                            disabled={!editable}
                        />
                    </div>

                    <div style={{ ...billPanelStyle, padding: '12px 20px', display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            <Tooltip
                                title={
                                    requestingReparse || (tracking && !reparseDone)
                                        ? 'Already re-parsing this bill'
                                        : reparsable
                                          ? ''
                                          : 'Re-parse is available once the current parsing run has finished'
                                }
                            >
                                <span>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={reparse}
                                        loading={requestingReparse}
                                        disabled={saving || !reparsable || requestingReparse || (tracking && !reparseDone)}
                                    >
                                        Re-parse bill
                                    </Button>
                                </span>
                            </Tooltip>
                        </div>
                        <Space>
                            <Button onClick={handleClose} disabled={saving || requestingReparse}>
                                Cancel
                            </Button>
                            <Tooltip
                                title={
                                    !editable
                                        ? 'This bill must be parsed before changes can be saved'
                                        : hasErrors
                                          ? 'Fix the highlighted fields before saving'
                                          : ''
                                }
                            >
                                <span>
                                    <Button
                                        type="primary"
                                        loading={saving}
                                        disabled={!editable || hasErrors || requestingReparse}
                                        onClick={() => onSave(form, verified && canVerify)}
                                    >
                                        Save changes
                                    </Button>
                                </span>
                            </Tooltip>
                        </Space>
                    </div>

                    <div style={{ ...billPanelStyle, padding: '8px 20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Parsed: {effectiveRow.parsedAt ? getComputerFormattedTime(effectiveRow.parsedAt) : EMPTY_VALUE}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            Last updated: {getComputerFormattedTime(effectiveRow.updatedAt)}
                        </Text>
                    </div>
                </div>
            </>
        </Modal>
    );
};
